import type { UseFormReturn } from 'react-hook-form';

import { Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { JobPostLocationType } from '~/api/jobPosts/constants';
import { Title } from '~/components';
import { Combobox, Field, Select } from '~/components/Form';

import type { JobPostForm } from '../types';

import { useExperienceLevelOptions, useLocationTypeOptions, useRegionOptions } from '../../hooks';
import { sectionStyle } from './jobPostSections.css';

type Props = {
  form: UseFormReturn<JobPostForm>;
};

export const JobPostDetailsSection = ({ form }: Props) => {
  const { t } = useTranslation(['jobPosts', 'validation']);

  const {
    control,
    formState: { isSubmitted },
    setValue,
    trigger,
    watch,
  } = form;

  const locationTypeItems = useLocationTypeOptions();
  const regionItems = useRegionOptions();
  const experienceLevelOptions = useExperienceLevelOptions();
  const experienceLevelItems = Combobox.useItems(experienceLevelOptions, {
    getLabel: (option) => option.label,
    getValue: (option) => option.value,
  });

  const isRemote = watch('locationType') === JobPostLocationType.Remote;

  return (
    <div className={sectionStyle}>
      <Title as="h2" styleAs="h3">
        {t('jobPosts:createEdit.sections.details.title')}
      </Title>

      {/* Location type */}
      <Controller
        control={control}
        name="locationType"
        render={({ field: { name, onChange, value }, fieldState: { error, invalid, isDirty, isTouched } }) => (
          <Field.Root dirty={isDirty} invalid={invalid} name={name} touched={isTouched}>
            <Field.Label>{t('jobPosts:fields.locationType.label')}</Field.Label>

            <Select.Root
              items={locationTypeItems}
              onValueChange={(locationType) => {
                onChange(locationType);

                if (locationType === JobPostLocationType.Remote) setValue('region', null);
                if (isSubmitted) trigger('region');
              }}
              value={value}
            >
              <Select.Button placeholder={t('jobPosts:fields.locationType.placeholder')} />

              <Select.List>
                {locationTypeItems.map((item) => (
                  <Select.Item key={item.value} value={item.value}>
                    {item.label}
                  </Select.Item>
                ))}
              </Select.List>
            </Select.Root>

            <Field.Error match={!!error}>{error?.message}</Field.Error>
          </Field.Root>
        )}
        rules={{ required: { value: true, message: t('validation:required') } }}
      />

      {/* Region */}
      <Controller
        control={control}
        name="region"
        render={({ field: { name, onChange, value }, fieldState: { error, invalid, isDirty, isTouched } }) => (
          <Field.Root dirty={isDirty} disabled={isRemote} invalid={invalid} name={name} touched={isTouched}>
            <Field.Label>{t('jobPosts:fields.region.label')}</Field.Label>

            {/* Not on the Controller: RHF syncs `disabled` one render later, so trigger('region') would skip validation */}
            <Select.Root disabled={isRemote} items={regionItems} onValueChange={onChange} value={value}>
              <Select.Button placeholder={isRemote ? undefined : t('jobPosts:fields.region.placeholder')} />

              <Select.List>
                {regionItems.map((item) => (
                  <Select.Item key={item.value} value={item.value}>
                    {item.label}
                  </Select.Item>
                ))}
              </Select.List>
            </Select.Root>

            <Field.Error match={!!error}>{error?.message}</Field.Error>
          </Field.Root>
        )}
        rules={{
          validate: (value, formValues) =>
            value !== null || formValues.locationType === JobPostLocationType.Remote || t('validation:required'),
        }}
      />

      {/* Experience level */}
      <Controller
        control={control}
        name="experienceLevel"
        render={({ field: { name, onChange, value }, fieldState: { error, invalid, isDirty, isTouched } }) => (
          <Field.Root dirty={isDirty} invalid={invalid} name={name} touched={isTouched}>
            <Field.Label optional>{t('jobPosts:fields.experienceLevel.label')}</Field.Label>

            <Combobox.Root items={experienceLevelItems} onValueChange={onChange} value={value}>
              <Combobox.Button placeholder={t('jobPosts:fields.experienceLevel.placeholder')} />

              <Combobox.List>
                {(item: (typeof experienceLevelOptions)[number]) => (
                  <Combobox.Item key={item.value} value={item.value}>
                    {item.label}
                  </Combobox.Item>
                )}
              </Combobox.List>
            </Combobox.Root>

            <Field.Error match={!!error}>{error?.message}</Field.Error>
          </Field.Root>
        )}
      />
    </div>
  );
};
